import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
	User,
	Mail,
	Phone,
	Lock,
	Shield,
	CheckCircle,
	Clock,
	ArrowLeft,
	Sparkles,
	Award,
	Sofa,
	Truck,
	Package,
	Key,
	Home,
	Star,
	Gift,
	ShieldCheck,
	CreditCard,
	Headphones,
	Calendar,
	MessageSquare
} from 'lucide-react';

// Import Firebase
import { initializeApp } from 'firebase/app';
import {
	getAuth,
	RecaptchaVerifier,
	signInWithPhoneNumber,
	PhoneAuthProvider,
	signInWithCredential
} from 'firebase/auth';

// Firebase configuration
const firebaseConfig = {
	apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
	authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
	projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
	storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
	messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
	appId: process.env.REACT_APP_FIREBASE_APP_ID,
	measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

const Register = () => {
	// Form states
	const [formData, setFormData] = useState({
		name: '',
		email: '',
		phone: '',
		password: '',
		confirmPassword: ''
	});
	const [stage, setStage] = useState('DETAILS'); // DETAILS or OTP
	const [otp, setOtp] = useState('');
	const [loading, setLoading] = useState(false);
	const [message, setMessage] = useState('');
	const [timer, setTimer] = useState(0);
	const [canResend, setCanResend] = useState(false);
	const [verificationId, setVerificationId] = useState('');
	const [confirmationResult, setConfirmationResult] = useState(null);
	const [animate, setAnimate] = useState(false);
	const [recaptchaVerifier, setRecaptchaVerifier] = useState(null);
	const navigate = useNavigate();

	// Initialize reCAPTCHA on component mount
	useEffect(() => {
		if (!window.recaptchaVerifier) {
			const verifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
				size: 'invisible',
				callback: () => {
					// reCAPTCHA solved
				}
			});
			window.recaptchaVerifier = verifier;
			setRecaptchaVerifier(verifier);
		}
	}, []);

	// Timer useEffect
	useEffect(() => {
		let interval;
		if (timer > 0) {
			interval = setInterval(() => {
				setTimer(prev => prev - 1);
			}, 1000);
		} else if (timer === 0 && stage === 'OTP') {
			setCanResend(true);
		}
		return () => clearInterval(interval);
	}, [timer, stage]);

	// Animation effect
	useEffect(() => {
		setAnimate(true);
		const timer = setTimeout(() => setAnimate(false), 1000);
		return () => clearTimeout(timer);
	}, [stage]);

	const validateForm = () => {
		const { name, email, phone, password, confirmPassword } = formData;

		if (!name || !email || !phone || !password || !confirmPassword) {
			return 'All fields are required.';
		}

		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!emailRegex.test(email)) {
			return 'Invalid email format.';
		}

		const phoneRegex = /^[0-9]{10}$/;
		if (!phoneRegex.test(phone)) {
			return 'Phone number must be exactly 10 digits.';
		}

		if (password.length < 6) {
			return 'Password must be at least 6 characters.';
		}

		if (password !== confirmPassword) {
			return 'Passwords do not match.';
		}

		return '';
	};

	const handleChange = (e) => {
		setFormData(prev => ({
			...prev,
			[e.target.name]: e.target.value
		}));
	};

	const handleOtpChange = (e) => {
		const value = e.target.value.replace(/\D/g, '').slice(0, 6);
		setOtp(value);
	};

	const handleSendOtp = async (e) => {
		e.preventDefault();
		setMessage('');

		const validationError = validateForm();
		if (validationError) {
			setMessage(validationError);
			return;
		}

		setLoading(true);
		try {
			// Format phone number for Firebase
			const phoneNumber = `+91${formData.phone}`;

			// Send OTP using Firebase
			const appVerifier = window.recaptchaVerifier;

			const confirmation = await signInWithPhoneNumber(auth, phoneNumber, appVerifier);
			setConfirmationResult(confirmation);

			toast.success('OTP sent successfully to your phone!');

			setStage('OTP');
			setTimer(60);
			setCanResend(false);
			setOtp('');
		} catch (error) {
			console.error('Firebase OTP Error:', error);

			let errorMessage = 'Failed to send OTP. Please try again.';

			if (error.code === 'auth/invalid-phone-number') {
				errorMessage = 'Invalid phone number format.';
			} else if (error.code === 'auth/too-many-requests') {
				errorMessage = 'Too many attempts. Please try again later.';
			} else if (error.code === 'auth/quota-exceeded') {
				errorMessage = 'OTP quota exceeded. Please try again later.';
			}

			setMessage(errorMessage);
			toast.error(errorMessage);
		} finally {
			setLoading(false);
		}
	};

	const handleResendOtp = async () => {
		if (!canResend) return;

		setLoading(true);
		try {
			const phoneNumber = `+91${formData.phone}`;
			const appVerifier = window.recaptchaVerifier;

			const confirmation = await signInWithPhoneNumber(auth, phoneNumber, appVerifier);
			setConfirmationResult(confirmation);

			toast.success('OTP resent successfully!');

			setTimer(60);
			setCanResend(false);
			setOtp('');
		} catch (error) {
			console.error('Firebase Resend OTP Error:', error);

			let errorMessage = 'Failed to resend OTP. Please try again.';

			if (error.code === 'auth/too-many-requests') {
				errorMessage = 'Too many attempts. Please try again later.';
			}

			setMessage(errorMessage);
			toast.error(errorMessage);
		} finally {
			setLoading(false);
		}
	};

	const handleVerifyOtp = async (e) => {
		e.preventDefault();

		if (otp.length !== 6) {
			setMessage('OTP must be 6 digits');
			return;
		}

		setLoading(true);
		try {
			// Verify OTP with Firebase
			const credential = PhoneAuthProvider.credential(confirmationResult.verificationId, otp);
			const userCredential = await signInWithCredential(auth, credential);

			// Get Firebase token
			const firebaseToken = await userCredential.user.getIdToken();

			// Call backend register endpoint with Firebase token
			const registerPayload = {
				name: formData.name,
				email: formData.email,
				phone: formData.phone,
				password: formData.password,
				firebaseToken: firebaseToken
			};

			const response = await axios.post(
				`${process.env.REACT_APP_BACKEND_URL}/api/buyer/register-with-phone`,
				registerPayload,
				{
					headers: {
						'Authorization': `Bearer ${firebaseToken}`
					}
				}
			);

			if (response.data.token) {
				// Store JWT token
				localStorage.setItem('token', response.data.token);
				localStorage.setItem('user', JSON.stringify(response.data.user));

				toast.success('Registration successful! Welcome to Zakkuu!');
				navigate("/login");
			}
		} catch (error) {
			console.error('Verification Error:', error);

			let errorMessage = 'Verification failed. Please try again.';

			if (error.code === 'auth/invalid-verification-code') {
				errorMessage = 'Invalid OTP code. Please check and try again.';
			} else if (error.code === 'auth/code-expired') {
				errorMessage = 'OTP has expired. Please request a new one.';
			} else if (error.response?.data?.msg) {
				errorMessage = error.response.data.msg;
			}

			setMessage(errorMessage);
			toast.error(errorMessage);
		} finally {
			setLoading(false);
		}
	};

	const handleBack = () => {
		setStage('DETAILS');
		setMessage('');
		setTimer(0);
		setCanResend(false);
		setVerificationId('');
		setConfirmationResult(null);
		setOtp('');
	};

	const formatTime = (seconds) => {
		const mins = Math.floor(seconds / 60);
		const secs = seconds % 60;
		return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
	};

	// reCAPTCHA container (hidden)
	useEffect(() => {
		const container = document.getElementById('recaptcha-container');
		if (container && !container.innerHTML) {
			container.style.display = 'none';
		}
	}, []);

	return (
		<div className="min-h-screen flex flex-col lg:flex-row">
			{/* Left side - Branding and Furniture Theme */}
			<div className="lg:w-1/2 bg-[#141210] p-8 lg:p-12 flex flex-col justify-start relative overflow-hidden border-r border-stone-800">

				{/* Artisan Background Texture - Subtle wood grain overlay */}
				<div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/wood-pattern.png')]"></div>

				{/* Decorative elements - Replaced neon with Soft Amber/Candlelight Glows */}
				<div className="absolute top-0 left-0 w-full h-full opacity-20 pointer-events-none">
					<div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-amber-900 blur-[120px]"></div>
					<div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-stone-800 blur-[120px]"></div>
					<div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full bg-orange-900/20 blur-[100px]"></div>
				</div>

				{/* Geometric patterns - Switched to Architectural "Blueprint" style */}
				<div className="absolute inset-0 opacity-10">
					<div className="absolute top-1/4 left-1/4 w-24 h-24 border border-stone-500 rotate-45"></div>
					<div className="absolute bottom-1/4 right-1/4 w-32 h-32 border border-stone-600 rotate-12"></div>
					<div className="absolute top-3/4 left-3/4 w-16 h-16 border border-stone-500 rotate-90"></div>
					<div className="absolute top-1/3 right-1/3 w-20 h-20 border border-stone-600 -rotate-45"></div>
				</div>

				{/* Logo and Brand Section - Wood-shop aesthetic */}
				<div className="relative z-10">
					<div className="flex items-center gap-4 mb-10">
						<div className="relative group">
							{/* Square, non-rounded container for the icon */}
							<div className="w-16 h-16 bg-[#2A2420] border border-stone-700 flex items-center justify-center shadow-2xl group-hover:border-amber-700 transition-colors duration-300">
								<Sofa className="w-9 h-9 text-stone-200" />
							</div>
							<div className="absolute -bottom-2 -right-2 w-8 h-8 bg-amber-700 flex items-center justify-center border border-[#141210]">
								<Sparkles className="w-4 h-4 text-white" />
							</div>
						</div>
						<div>
							<img src="/assets/zakkuu logo bg.png" alt="Logo" className="h-10 brightness-0 invert opacity-80" />
							<div className="flex items-center gap-2 mt-1">
								<div className="w-2 h-2 bg-amber-600 rotate-45"></div>
								<p className="text-stone-400 text-sm uppercase tracking-[0.2em] font-bold">Premium Furniture & Home Decor</p>
							</div>
						</div>
					</div>

					<div className="mb-10">
						<div className="inline-flex items-center gap-3 px-6 py-2 bg-stone-900/50 backdrop-blur-sm border border-stone-800 mb-6">
							<Award className="w-4 h-4 text-amber-500" />
							<span className="text-stone-300 text-xs uppercase tracking-widest font-bold">Exclusive Artisan Benefits</span>
							<Gift className="w-4 h-4 text-amber-500" />
						</div>
						<h2 className="text-5xl lg:text-6xl font-serif font-bold text-stone-100 mb-6 leading-tight">
							Create Your
							<span className="block text-amber-200 italic mt-3">Dream Home Oasis</span>
						</h2>
						<p className="text-stone-400 text-xl leading-relaxed mb-8 max-w-2xl font-serif">
							Join thousands of interior enthusiasts who have transformed their living spaces
							with our curated collection of premium furniture. Experience luxury, comfort,
							and style in every piece we craft.
						</p>

						{/* Stats Section - Minimalist "Gallery" style */}
						<div className="flex flex-wrap gap-10 mb-10 border-y border-stone-800 py-8">
							<div className="flex flex-col gap-1">
								<span className="text-3xl font-serif text-stone-100">10K+</span>
								<span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Happy Customers</span>
							</div>
							<div className="flex flex-col gap-1">
								<span className="text-3xl font-serif text-stone-100">5★</span>
								<span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Rated Service</span>
							</div>
							<div className="flex flex-col gap-1">
								<span className="text-3xl font-serif text-stone-100">10+</span>
								<span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Years of Excellence</span>
							</div>
						</div>
					</div>
				</div>

				{/* Features List - "Wood Panel" cards */}
				<div className="relative z-10">
					<h3 className="text-xs uppercase tracking-[0.3em] text-amber-600 font-bold mb-8 flex items-center gap-4">
						<div className="h-[1px] w-8 bg-amber-800"></div>
						Why Join Zakkuu Family?
					</h3>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">

						{/* Feature Item Template */}
						{[
							{ icon: User, title: "Personalized Experience", desc: "Customized recommendations based on your style and favorite designs." },
							{ icon: Truck, title: "Premium Delivery", desc: "White-glove delivery service with assembly included. Handled with care." },
							{ icon: ShieldCheck, title: "Extended Warranty", desc: "Enjoy 5-year warranty on all products plus premium concierge support." },
							{ icon: CreditCard, title: "Flexible Payment", desc: "EMI options and multiple secure payment methods for your convenience." }
						].map((feature, idx) => (
							<div key={idx} className="flex items-start gap-4 p-5 bg-stone-900/30 border border-stone-800 hover:border-amber-900/50 transition-all duration-300">
								<div className="w-10 h-10 border border-stone-700 flex items-center justify-center flex-shrink-0 group-hover:bg-amber-900/20 transition-colors">
									<feature.icon className="w-5 h-5 text-amber-600" />
								</div>
								<div>
									<h4 className="font-bold text-stone-200 text-sm uppercase tracking-wider mb-1">{feature.title}</h4>
									<p className="text-stone-500 text-xs leading-relaxed font-serif">
										{feature.desc}
									</p>
								</div>
							</div>
						))}

					</div>
				</div>
			</div>

			{/* Right side - Register Form */}
			<div className="lg:w-1/2 p-6 sm:p-8 lg:p-12 flex items-center justify-center bg-[#fdfcf8]">
				<div className={`w-full max-w-lg transition-all duration-500 ${animate ? 'scale-105' : 'scale-100'}`}>
					{/* Main Card with a "Wooden Plank" feel */}
					<div className="bg-white rounded-3xl shadow-[0_20px_50px_rgba(67,41,13,0.15)] p-8 border border-stone-200 relative overflow-hidden">
						{/* Subtle wood grain overlay effect could be added via CSS class */}

						{/* Hidden reCAPTCHA container */}
						<div id="recaptcha-container"></div>

						{/* Artisan Form Header */}
						<div className="text-center mb-10">
							<div className="relative inline-block mb-6">
								{/* Amber Glow */}
								<div className="absolute inset-0 bg-gradient-to-r from-amber-400 to-orange-500 rounded-3xl blur-xl opacity-20"></div>
								<div className="relative w-20 h-20 bg-stone-100 rounded-3xl flex items-center justify-center shadow-md border border-stone-200">
									<div className="w-14 h-14 bg-gradient-to-br from-amber-700 to-orange-900 rounded-2xl flex items-center justify-center shadow-inner">
										<Key className="w-8 h-8 text-stone-100" />
									</div>
								</div>
							</div>
							<h2 className="text-3xl sm:text-4xl font-serif font-bold text-stone-800 mb-3 italic">
								{stage === 'DETAILS' ? 'Begin Your Design Journey' : 'Secure Verification'}
							</h2>
							<p className="text-stone-600 text-lg mb-6 max-w-md mx-auto leading-relaxed">
								{stage === 'DETAILS'
									? 'Create your account to unlock exclusive designs, personalized recommendations, and member-only offers.'
									: 'For your security, please enter the 6-digit verification code sent to your phone.'}
							</p>

							{/* Progress Indicator - Earth Tones */}
							<div className="flex items-center justify-center gap-4 mb-8">
								<div className={`w-3 h-3 rounded-full ${stage === 'DETAILS' ? 'bg-amber-700' : 'bg-emerald-600'}`}></div>
								<div className="w-16 h-1 bg-stone-200 rounded-full">
									<div className={`h-full rounded-full transition-all duration-500 ${stage === 'DETAILS' ? 'w-1/2 bg-amber-400' : 'w-full bg-emerald-500'}`}></div>
								</div>
								<div className={`w-3 h-3 rounded-full ${stage === 'DETAILS' ? 'bg-stone-300' : 'bg-amber-700'}`}></div>
								<span className="text-sm text-stone-500 font-medium ml-2 font-mono">
									Step {stage === 'DETAILS' ? '1 of 2' : '2 of 2'}
								</span>
							</div>
						</div>

						{/* Register Form */}
						<form onSubmit={stage === 'DETAILS' ? handleSendOtp : handleVerifyOtp} className="space-y-8">
							{stage === 'DETAILS' ? (
								<div className="space-y-6">
									<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
										<div>
											<label className="flex items-center gap-2 text-xs font-bold text-stone-500 mb-3 uppercase tracking-[0.2em]">
												<User className="w-4 h-4 text-amber-700" />
												Full Name
											</label>
											<div className="relative group">
												<input
													type="text"
													name="name"
													value={formData.name}
													onChange={handleChange}
													className="w-full px-5 py-4 bg-stone-50 border-2 border-stone-200 rounded-xl focus:border-amber-600 focus:ring-4 focus:ring-amber-50 transition-all duration-300 text-lg group-hover:border-stone-300 placeholder-stone-400"
													placeholder="Enter your full name"
													required
												/>
												<div className="absolute right-4 top-1/2 transform -translate-y-1/2">
													<User className="w-5 h-5 text-stone-300" />
												</div>
											</div>
										</div>

										<div>
											<label className="flex items-center gap-2 text-xs font-bold text-stone-500 mb-3 uppercase tracking-[0.2em]">
												<Mail className="w-4 h-4 text-amber-700" />
												Email Address
											</label>
											<div className="relative group">
												<input
													type="email"
													name="email"
													value={formData.email}
													onChange={handleChange}
													className="w-full px-5 py-4 bg-stone-50 border-2 border-stone-200 rounded-xl focus:border-amber-600 focus:ring-4 focus:ring-amber-50 transition-all duration-300 text-lg group-hover:border-stone-300 placeholder-stone-400"
													placeholder="your.email@example.com"
													required
												/>
												<div className="absolute right-4 top-1/2 transform -translate-y-1/2">
													<Mail className="w-5 h-5 text-stone-300" />
												</div>
											</div>
										</div>
									</div>

									<div>
										<label className="flex items-center gap-2 text-xs font-bold text-stone-500 mb-3 uppercase tracking-[0.2em]">
											<Phone className="w-4 h-4 text-amber-700" />
											Phone Number
										</label>
										<div className="relative group">
											<div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
												<div className="flex items-center gap-3">
													<span className="text-stone-700 font-bold text-lg">+91</span>
													<div className="w-px h-6 bg-stone-300"></div>
												</div>
											</div>
											<input
												type="tel"
												name="phone"
												value={formData.phone}
												onChange={handleChange}
												className="w-full pl-20 pr-12 py-4 bg-stone-50 border-2 border-stone-200 rounded-xl focus:border-amber-600 focus:ring-4 focus:ring-amber-50 transition-all duration-300 text-lg group-hover:border-stone-300"
												placeholder="98765 43210"
												maxLength="10"
												required
											/>
											<div className="absolute right-4 top-1/2 transform -translate-y-1/2">
												<Phone className="w-5 h-5 text-stone-300" />
											</div>
										</div>
										<p className="text-stone-400 text-xs mt-2 italic">We'll send a verification code via SMS</p>
									</div>

									<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
										<div>
											<label className="flex items-center gap-2 text-xs font-bold text-stone-500 mb-3 uppercase tracking-[0.2em]">
												<Lock className="w-4 h-4 text-amber-700" />
												Password
											</label>
											<div className="relative group">
												<input
													type="password"
													name="password"
													value={formData.password}
													onChange={handleChange}
													className="w-full px-5 py-4 bg-stone-50 border-2 border-stone-200 rounded-xl focus:border-amber-600 focus:ring-4 focus:ring-amber-50 transition-all duration-300 text-lg group-hover:border-stone-300"
													placeholder="••••••••"
													required
												/>
												<div className="absolute right-4 top-1/2 transform -translate-y-1/2">
													<Lock className="w-5 h-5 text-stone-300" />
												</div>
											</div>
										</div>

										<div>
											<label className="flex items-center gap-2 text-xs font-bold text-stone-500 mb-3 uppercase tracking-[0.2em]">
												<CheckCircle className="w-4 h-4 text-amber-700" />
												Confirm Password
											</label>
											<div className="relative group">
												<input
													type="password"
													name="confirmPassword"
													value={formData.confirmPassword}
													onChange={handleChange}
													className="w-full px-5 py-4 bg-stone-50 border-2 border-stone-200 rounded-xl focus:border-amber-600 focus:ring-4 focus:ring-amber-50 transition-all duration-300 text-lg group-hover:border-stone-300"
													placeholder="••••••••"
													required
												/>
												<div className="absolute right-4 top-1/2 transform -translate-y-1/2">
													<CheckCircle className="w-5 h-5 text-stone-300" />
												</div>
											</div>
										</div>
									</div>

									{/* Password Strength - Wood Grain Theme */}
									<div className="space-y-2">
										<div className="flex justify-between text-sm">
											<span className="text-stone-600">Password Strength</span>
											<span className={`font-bold ${formData.password.length >= 6 ? 'text-amber-700' : 'text-stone-400'}`}>
												{formData.password.length >= 6 ? 'Strong' : 'Weak'}
											</span>
										</div>
										<div className="h-2 bg-stone-100 rounded-full overflow-hidden border border-stone-200">
											<div
												className={`h-full rounded-full transition-all duration-300 ${formData.password.length >= 8 ? 'bg-amber-800 w-full' :
													formData.password.length >= 6 ? 'bg-amber-500 w-2/3' :
														'bg-orange-300 w-1/3'
													}`}
											></div>
										</div>
									</div>

									{message && (
										<div className="p-4 bg-orange-50 border border-orange-200 rounded-2xl">
											<p className="text-orange-800 text-sm font-medium text-center">{message}</p>
										</div>
									)}

									{/* Terms Agreement */}
									<div className="flex items-start gap-3">
										<input
											type="checkbox"
											id="terms"
											required
											className="w-5 h-5 text-amber-800 bg-stone-50 border-stone-300 rounded focus:ring-amber-600 focus:ring-2 mt-1"
										/>
										<label htmlFor="terms" className="text-stone-600 text-sm leading-relaxed">
											I agree to Zakkuu's{' '}
											<a href="/terms-conditions" className="text-amber-800 underline decoration-amber-200 hover:decoration-amber-800 font-bold">Terms of Service</a>{' '}
											and{' '}
											<a href="/privacy-policy" className="text-amber-800 underline decoration-amber-200 hover:decoration-amber-800 font-bold">Privacy Policy</a>.
										</label>
									</div>

									<button
										type="submit"
										disabled={loading}
										className="w-full py-4 bg-gradient-to-r from-amber-800 to-orange-950 text-stone-100 font-bold rounded-xl hover:from-amber-900 hover:to-black transition-all duration-300 transform hover:scale-[1.01] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 shadow-lg shadow-stone-200 text-lg tracking-wide"
									>
										{loading ? (
											<>
												<svg className="animate-spin h-6 w-6 text-stone-100" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
													<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
													<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
												</svg>
												Carving your account...
											</>
										) : (
											<>
												<MessageSquare className="w-6 h-6" />
												Send OTP & Continue
											</>
										)}
									</button>
								</div>
							) : (
								<div className="space-y-8">
									{/* OTP Stage - Parchment feel */}
									<div className="text-center p-6 bg-stone-50 rounded-2xl border-2 border-stone-200 shadow-inner">
										<div className="flex items-center justify-center gap-3 mb-3">
											<MessageSquare className="w-5 h-5 text-amber-700" />
											<p className="text-stone-600 font-bold">OTP sent to your workshop mobile</p>
										</div>
										<p className="text-2xl font-bold text-stone-800 tracking-widest font-mono">
											+91 ••••• •{formData.phone.slice(-3)}
										</p>
										<p className="text-stone-400 text-sm mt-3 italic">
											Code expires in 10 minutes.
										</p>
									</div>

									<div>
										<label className="block text-xs font-bold text-stone-500 mb-4 text-center uppercase tracking-[0.3em]">
											6-Digit Verification Code
										</label>
										<div className="relative">
											<input
												type="text"
												value={otp}
												onChange={handleOtpChange}
												className="w-full px-5 py-5 text-center text-4xl font-black tracking-[0.5em] bg-stone-50 border-2 border-stone-200 rounded-2xl focus:border-amber-600 focus:ring-4 focus:ring-amber-50 transition-all duration-300 font-mono text-stone-800"
												placeholder="000000"
												maxLength={6}
												inputMode="numeric"
												autoFocus
											/>
											<div className="absolute right-5 top-1/2 transform -translate-y-1/2">
												<ShieldCheck className="w-6 h-6 text-amber-600" />
											</div>
										</div>
									</div>

									<div className="flex justify-between items-center">
										<button
											type="button"
											onClick={handleResendOtp}
											disabled={!canResend || loading}
											className={`text-sm font-bold flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${canResend
												? 'text-amber-800 hover:bg-amber-50'
												: 'text-stone-400 bg-stone-100'
												}`}
										>
											{canResend ? (
												<>
													<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
													</svg>
													Resend OTP
												</>
											) : (
												<>
													<Clock className="w-5 h-5" />
													Wait {formatTime(timer)}
												</>
											)}
										</button>

										<button
											type="button"
											onClick={handleBack}
											className="text-sm text-stone-500 hover:text-stone-800 hover:bg-stone-100 font-bold flex items-center gap-2 px-4 py-3 rounded-xl transition-all duration-300"
										>
											<ArrowLeft className="w-4 h-4" />
											Edit Number
										</button>
									</div>

									<div className="flex gap-4">
										<button
											type="button"
											onClick={handleBack}
											className="flex-1 py-4 border-2 border-stone-200 text-stone-600 font-bold rounded-xl hover:bg-stone-50 transition-all duration-300 flex items-center justify-center gap-3"
											disabled={loading}
										>
											<ArrowLeft className="w-5 h-5" />
											Back
										</button>
										<button
											type="submit"
											disabled={loading || otp.length !== 6}
											className="flex-1 py-4 bg-gradient-to-r from-amber-800 to-orange-950 text-stone-100 font-bold rounded-xl hover:from-amber-900 hover:to-black transition-all duration-300 transform hover:scale-[1.02] shadow-lg shadow-stone-200 flex items-center justify-center gap-3"
										>
											{loading ? 'Verifying...' : 'Complete Registration'}
										</button>
									</div>
								</div>
							)}
						</form>

						{/* Artisan Divider */}
						<div className="my-8">
							<div className="relative">
								<div className="absolute inset-0 flex items-center">
									<div className="w-full border-t border-stone-200"></div>
								</div>
								<div className="relative flex justify-center">
									<span className="px-6 bg-white text-stone-400 font-serif italic text-sm">
										{stage === 'DETAILS' ? 'Already part of our community?' : 'Almost done!'}
									</span>
								</div>
							</div>
						</div>

						{/* Footer Links */}
						<div className="text-center space-y-6">
							{stage === 'DETAILS' && (
								<Link
									to="/login"
									className="inline-block w-full py-4 border-2 border-stone-800 text-stone-800 font-bold rounded-xl hover:bg-stone-800 hover:text-stone-100 transition-all duration-300 flex items-center justify-center gap-3"
								>
									<User className="w-6 h-6" />
									Sign In to Your Account
								</Link>
							)}

							<div className="grid grid-cols-2 gap-4">
								<div className="p-4 bg-stone-50 rounded-xl border border-stone-200">
									<div className="flex items-center gap-3 mb-1">
										<Shield className="w-4 h-4 text-amber-800" />
										<span className="text-stone-800 font-bold text-xs">Secure Shop</span>
									</div>
									<p className="text-stone-500 text-[10px] uppercase tracking-widest">SSL Encrypted</p>
								</div>
								<div className="p-4 bg-stone-50 rounded-xl border border-stone-200">
									<div className="flex items-center gap-3 mb-1">
										<Headphones className="w-4 h-4 text-amber-800" />
										<span className="text-stone-800 font-bold text-xs">Support</span>
									</div>
									<p className="text-stone-500 text-[10px] uppercase tracking-widest">Craftsman Help</p>
								</div>
							</div>

							<div className="pt-4 border-t border-stone-100">
								<a
									href="/support"
									className="inline-flex items-center gap-2 text-stone-400 hover:text-amber-800 transition-colors duration-300 text-sm font-bold"
								>
									Contact Our Support Team
								</a>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Register;
